<template>
  <div class="samples">
    <div class="width-responsiveness flex justify-between align-center mt-4 samples__subheader">
      <h2>Listagem de Amostras</h2>
      <div class="flex align-center gap-3">
        <PrinterConnectionStatus
          class="mr-n3"
          label-text="Status:"
          @download-plugin-requested="handleDownloadPluginRequested"
        />
        <PrinterConfigButton
          v-if="toggle.printerPlugin && webSocketStatus.connectionState === 'connected'"
          tooltip-text="Configurações da Impressora"
          tooltip-direction="left"
          class="mr-1"
        />

        <printer-calibration />

        <PrinterHelpButton />
      </div>
    </div>

    <SamplesFilter ref="samplesFilter">
      <template #actions>
        <div class="flex justify-end align-center w-full">
          <div class="flex align-end gap-2">
            <button
              v-if="samplesCurrentTab === 'aol20'"
              id="btnPrintSamplesLabel"
              class="btn btn-primary btn-regular-size"
              :disabled="!hasSamples"
              @click="loadingSamplesReceipt"
            >
              <svg-loader
                class="icon-print"
                src="/static/images/icon-print-alternative-white.svg"
              />
              IMPRIMIR RECIBO
            </button>
            <button
              v-if="toggle.printerPlugin"
              id="btnPrintBatchLabel"
              class="btn btn-primary btn-regular-size"
              :disabled="!hasSamples"
              @click="webSocketPrintSamplesSelected()"
            >
              <svg-loader
                class="icon-print"
                src="/static/images/icon-print-alternative-white.svg"
              />
              IMPRIMIR ETIQUETAS
              <span v-if="samplesSelected.length > 0">({{ samplesSelected.length }})</span>
            </button>
            <button
              v-else
              id="btnPrintBatchLabel"
              class="btn btn-primary btn-regular-size"
              :disabled="!hasSamples"
              @click="printSamplesSelected()"
            >
              <svg-loader
                class="icon-print"
                src="/static/images/icon-print-alternative-white.svg"
              />
              IMPRIMIR ETIQUETAS
              <span v-if="samplesSelected.length > 0">({{ samplesSelected.length }})</span>
            </button>
          </div>
        </div>
      </template>
    </SamplesFilter>

    <md-layout class="justify-end px-10 ml-13 mt-4">
      <div class="switcher-position">
        <view-switcher
          option="grid"
          class="mb-n8"
          tooltip-direction="left"
          @switch-view="pushToBatches()"
        />
      </div>
    </md-layout>

    <div class="width-responsiveness">
      <fake-tab
        :first-active="firstTabSelected"
        first-title="ORIGEM AOL"
        first-title-tooltip="Cadastros originados na plataforma 2.0."
        secound-title="ORIGEM LIS"
        secound-title-tooltip="Cadastros originados no sistema LIS do cliente."
        :disabled="samplesLoading"
        @update="fakeTabUpdate"
      />

      <div slot="alert">
        <Alert
          v-if="samplesCurrentTab === 'aolWs'"
          :alert-text="alertSamples"
          item-name-in-storage="alertSamplesClosed"
          icon="icon-doc-required.svg"
        />
      </div>

      <div class="print-tag-main-view__row-filter">
        <div class="print-tag-main-view__form-patient-name">
          <div class="search-box-container mb-6">
            <input
              id="searchPatient"
              v-model="filterInput"
              placeholder="Filtrar listagem..."
              class="search-box__input"
            />
            <img
              src="/static/images/icon-search-purple.svg"
              class="search-box__icon"
            />
          </div>
        </div>
        <document-filter
          v-if="samplesCurrentTab !== 'aolWs'"
          class="print-tag-main-view__filter"
          :loading="samplesLoading"
          :total-exams-pending="totalSampleExamsWithDocumentsPending"
        />
      </div>
    </div>

    <samples-list
      v-if="hasSamples || samplesLoading"
      :filter-input="filterInput"
    />

    <empty-cart
      v-else
      description="Nenhuma amostra encontrada de acordo com a busca realizada."
      img="/static/images/icon-empty-cart.svg"
      class="print-tag-main-view__empty-cart"
    />

    <SampleModalConfirmation
      ref="samplesQuestionModal"
      @closeModal="clearSamplesSelected"
      @printTags="print"
    />

    <PrinterModal ref="printerModal" />

    <PrinterPluginDownloadModal
      ref="printerPluginDownloadModal"
      ref-name="printerPluginDownloadModal"
    />
  </div>
</template>

<script>
import PrinterCalibration from '@/components/common/calibration/PrinterCalibration';
import SamplesList from '@/components/PrintTag/Samples/SamplesList';
import EmptyCart from '@/components/common/EmptyCart';
import { mapGetters, mapActions } from 'vuex';
import { SAMPLES_SELECTED, eventBus } from '@/api/eventbus';
import ViewSwitcher from '@/components/common/ViewSwitcher';
import Snackbar from '@/components/Snackbar/Snackbar';
import FakeTab from '../../common/FakeTab';
import SampleModalConfirmation from './Components/SampleModalConfirmation';
import DocumentFilter from '../../Documents/Laboratory/Filter';
import Alert from '@/components/common/Alert';
import strings from '@/strings';
import label from '@/api/label';
import pdfkit from '../../../libs/pdfkit';
import blobstream from '../../../libs/blob-stream';
import SamplesFilter from './SamplesFilter.vue';
import SubHeader from '@/components/common/SubHeader';
import PrinterModal from '@/components/common/printer/PrinterModal';
import PrinterConfigButton from '../../common/printer/PrinterConfigButton.vue';
import PrinterConnectionStatus from '@/components/common/printer/PrinterConnectionStatus.vue';
import PrinterPluginDownloadModal from '@/components/common/printer/PrinterPluginDownloadModal.vue';
import PrinterHelpButton from '../../common/printer/PrinterHelpButton.vue';
import printerValidationMixin from '../printerValidationMixin';

export default {
  name: 'Batches',
  components: {
    'samples-list': SamplesList,
    'empty-cart': EmptyCart,
    'printer-calibration': PrinterCalibration,
    'view-switcher': ViewSwitcher,
    snackbar: Snackbar,
    'document-filter': DocumentFilter,
    'fake-tab': FakeTab,
    SampleModalConfirmation,
    Alert,
    SamplesFilter,
    SubHeader,
    PrinterModal,
    PrinterConfigButton,
    PrinterConnectionStatus,
    PrinterPluginDownloadModal,
    PrinterHelpButton,
  },
  mixins: [printerValidationMixin],
  data: () => ({
    filterInput: '',
    samplesSelected: [],
    alertSamples: strings.alerts.alertRequiredDocs,
  }),
  computed: {
    ...mapGetters([
      'samplesLoading',
      'samples',
      'sampleLoadingStatus',
      'samples',
      'samplesCounters',
      'samplesCurrentTab',
      'hasSamples',
      'totalSampleExamsWithDocumentsPending',
      'profile',
      'samplesStateDataSelected',
      'webSocketStatus',
      'defaultPrinter',
      'toggle',
    ]),
    firstTabSelected() {
      return this.samplesCurrentTab === 'aol20';
    },
  },
  watch: {
    samplesCurrentTab(newVal, oldVal) {
      this.updateUrl({ old: oldVal, new: newVal });
    },
  },
  created() {
    this.selectSampleList({ ...this.samplesStateDataSelected, ...this.$route.query });
    let self = this;
    eventBus.$on(SAMPLES_SELECTED, function (value) {
      self.samplesSelected = value;
    });
  },
  mounted() {
    if (this.$route.query.tab) {
      this.sampleSetTab(this.$route.query.tab);
    }
  },
  beforeDestroy() {
    this.samplesSelected = [];
  },
  methods: {
    ...mapActions(['printSamples', 'sampleSetTab', 'printReceipt', 'selectSampleList', 'clearAllSampleData', 'selectSampleList', 'getServiceSample', 'webSocketPrintSamples']),
    handleSearch() {
      let query = '/print-tag/samples?';
      let q = this.patientInput;
      q = q.replace(/[%¨!@#$&*()+=\\-_]/gi, '');
      query = query.concat('patientName=' + q);
      this.$router.push(query);
    },
    fakeTabUpdate(val) {
      if (val) {
        this.sampleSetTab('aol20');
        this.selectSampleList({ ...this.samplesStateDataSelected, offset: 0 });
      } else {
        this.sampleSetTab('aolWs');
        this.pushEventToAnalytics('Ver amostras Integração Direta');
      }

      this.getServiceSample();
    },
    loadingSamplesReceipt() {
      this.$parent.$refs.loadingReceiptModal.open();
      this.printReceipt(this.samplesSelected).then(() => {
        this.$parent.$refs.loadingReceiptModal.close();
      });
      this.pushEventToAnalytics('Impressão de recibo de amostra');
    },
    printSamplesSelected() {
      let samplesToPrint = this.samples;

      if (this.samplesSelected.length === 0 && this.samplesCurrentTab !== 'aolWs') {
        let samplesIds = samplesToPrint.map(sample => {
          return sample.sample_id;
        });
        this.samplesSelected = samplesIds;
      }

      if (this.samplesCurrentTab === 'aolWs') {
        if (!this.samplesSelected.length) {
          this.samplesSelected = samplesToPrint.map(sample => {
            return sample.bar_code;
          });
        }
        samplesToPrint = { barcodeList: this.samplesSelected };

        this.print('no', samplesToPrint);
      } else {
        let samplesObject = this.getSamplesObjectById(this.samplesSelected);
        samplesToPrint = samplesObject.map(function (elem) {
          return {
            sample_id: elem.sample_id,
            bar_code: elem.bar_code,
            batch: {
              label: elem.batch_label,
              id: elem.batch_id,
            },
          };
        });

        this.$refs.samplesQuestionModal.openModalConfirmation(samplesToPrint);
      }

      this.pushEventToAnalytics('Impressão de amostras ' + this.samplesCurrentTab);
    },

    async webSocketPrintSamplesSelected() {
      await this.executeWithPrinterValidation(() => {
        let samplesToPrint = this.samples;
        let batchIds = [];
        let samplesSelected = [];

        if (this.samplesSelected.length === 0) {
          let samplesIds = [];

          if (this.samplesCurrentTab === 'aolWs') {
            samplesIds = samplesToPrint.map(elem => {
              return elem.barcode || elem.bar_code;
            });
          } else {
            samplesIds = samplesToPrint.map(elem => {
              return elem.sample_id;
            });
          }

          batchIds = samplesToPrint.map(elem => {
            return elem.batch_identifier;
          });

          this.samplesSelected = samplesIds;

          samplesToPrint = { barcodeList: this.samplesSelected, batchList: batchIds };
        }

        if (this.samplesCurrentTab === 'aolWs') {
          const batchIds = this.samplesSelected
            .map(code => {
              const sample = this.samples.find(sample => sample.bar_code === code);
              return sample.batch_identifier;
            })
            .filter(Boolean);

          samplesToPrint = [
            {
              barcodeList: this.samplesSelected,
              batchList: [...new Set(batchIds)],
            },
          ];

          this.print('no', samplesToPrint);
        } else {
          let samplesObject = this.getSamplesObjectById(this.samplesSelected);

          batchIds = samplesObject.map(elem => {
            return elem.batch_identifier;
          });

          samplesSelected = samplesObject.map(elem => {
            return elem.barcode || elem.bar_code;
          });

          samplesToPrint = samplesObject.map(function (elem) {
            return {
              sample_id: elem.sample_id,
              bar_code: elem.bar_code,
              batch: {
                label: elem.batch_label,
                id: elem.batch_id,
              },
              batchList: batchIds,
              barcodeList: samplesSelected,
            };
          });

          this.$refs.samplesQuestionModal.openModalConfirmation(samplesToPrint);
        }
      });

      this.pushEventToAnalytics('Impressão de amostras ' + this.samplesCurrentTab);
    },

    createSampleLabel(doc, element, indice, ind, stream) {
      let str = element.label;
      let regex, label, name, infos, barcode, exam, examDescription, mnemonic, urgent, minimumVolume;
      let labelType = str.substring(1, 3);

      if (labelType === 'XA') {
        regex = /(?<=FD)(.*?)(?=FS)/g;
        label = str.match(regex);
        name = label[0].replace(/['"]+/g, '').replace(/['^]+/g, '');
        infos = label[1].replace(/['"]+/g, '').replace(/['^]+/g, '');
        barcode = label[3].replace(/['"]+/g, '').replace(/['^]+/g, '');
        exam = label[4].replace(/['"]+/g, '').replace(/['^]+/g, '');
        examDescription = label[5].replace(/['"]+/g, '').replace(/['^]+/g, '');
        mnemonic = label[6].replace(/['"]+/g, '').replace(/['^]+/g, '');
      } else {
        regex = /"(?:\\"|[^"])*"/g;
        label = str.match(regex);
        name = label[0].replace(/['"]+/g, '');
        infos = label[1].replace(/['"]+/g, '');
        barcode = label[3].replace(/['"]+/g, '');
        exam = label[4].replace(/['"]+/g, '');
        examDescription = label[5].replace(/['"]+/g, '');
        mnemonic = label[7].replace(/['"]+/g, '');
        urgent = label[8].replace(/['"]+/g, '').replace(/['^]+/g, '');
        minimumVolume = label[6].replace(/['"]+/g, '');
      }
      doc.fontSize(6).text(name, {
        width: 150,
        height: 10,
        align: 'left',
      });
      doc.fontSize(6).text(infos, {
        width: 150,
        height: 15,
        align: 'left',
      });
      doc.image(this.textToBase64Barcode(barcode, 'itf'), 15, 25, { width: 120, height: 30 });
      doc.fontSize(6).text(exam, 10, 55, {
        align: 'left',
      });
      doc.fontSize(6).text(examDescription, 10, 62, {
        align: 'left',
      });
      if (minimumVolume !== '') {
        doc.fontSize(6).text('VOLUME MÍNIMO:' + minimumVolume, 10, 68, {
          align: 'left',
        });
      }
      if (urgent !== '') {
        doc.save();
        doc.rotate(90, { origin: [160, 20] });
        doc.rect(160, 30, 35, 8).fillAndStroke('#black', '#000');
        doc.fillColor('white').fontSize(6).text(urgent, 160, 32).fill('black');
        doc.rotate(90 * -1, { origin: [160, 20] });
        doc.restore();
      }
      doc.rect(123, 57, 30, 10).fillAndStroke('#black', '#000');
      doc.fillColor('white').fontSize(8).text(mnemonic, 123, 59).fill('black');
      if (ind < indice) {
        doc.addPage();
      }
      if (ind === indice) {
        doc.end();
        stream.on('finish', function () {
          const url = stream.toBlobURL('application/pdf');
          window.open(url);
        });
      }
    },
    textToBase64Barcode(barcode, format) {
      var JsBarcode = require('jsbarcode');
      var canvas = document.createElement('canvas');
      JsBarcode(canvas, barcode, { format: format, displayValue: false });
      return canvas.toDataURL('image/png');
    },
    getSamplesObjectById(ids) {
      let objects = [];
      for (let i = 0; i < ids.length; i++) {
        objects.push(this.samples.filter(x => x.sample_id === ids[i])[0]);
      }
      return objects;
    },
    openSamplesAolSnackbar() {
      this.$nextTick(function () {
        this.$refs.samplesAol.open();
      });
    },
    pushToBatches() {
      const queryParams = {};
      const sampleFilters = this.$refs.samplesFilter.getCurrentFilters();

      if (sampleFilters.startDate) queryParams.startDate = sampleFilters.startDate;
      if (sampleFilters.endDate) queryParams.endDate = sampleFilters.endDate;
      if (sampleFilters.dateType) queryParams.dateType = sampleFilters.dateType;

      if (sampleFilters.packagingType) {
        queryParams.packagingType = sampleFilters.packagingType;
      }

      this.$router.push({
        path: '/print-tag/batches',
        query: queryParams,
      });
      this.pushEventToAnalytics('Visualizar como card');
    },
    pushEventToAnalytics(action) {
      this.$analytics.pushAnalyticsEvent('ga-event', {
        category: 'Impressão de Etiquetas - Amostras',
        action: action,
        'id-user': this.profile.id,
        'id-lab': this.profile.id_laboratory,
        'name-lab': this.profile.laboratoryName,
      });
    },
    updateUrl(params) {
      let url = this.$route.fullPath;
      url = url.replace(params.old, params.new);
      history.pushState({}, null, url);
    },
    clearSamplesSelected() {
      if (this.samples.length === this.samplesSelected.length) {
        eventBus.$emit(SAMPLES_SELECTED, []);
      }
    },
    async print(answer, samplesToPrint) {
      const source = this.samplesCurrentTab.toUpperCase();

      try {
        if (this.toggle.printerPlugin) {
          await this.webSocketPrintSamples({
            samplesId: samplesToPrint,
            printBatch: answer === 'yes',
            batch: {
              source,
            },
          });

          await this.getServiceSample();
        } else if (this.toggle.webPrinting) {
          const doc = new pdfkit({ size: [165.86, 83.73] });
          const stream = doc.pipe(blobstream());
          var indice;
          var ind = 0;
          samplesToPrint.forEach((element, index) => {
            indice = index;
            label.getLabel(element.bar_code).then(response => {
              var samples = response.data;
              samples.forEach(async element => {
                this.createSampleLabel(doc, element, indice, ind, stream);
                ind++;
              });
            });
          });
        } else {
          this.printSamples({
            samplesId: samplesToPrint,
            printBatch: answer === 'yes',
            source: this.samplesCurrentTab.toUpperCase(),
          });
        }
      } catch (error) {
        console.error('Erro na impressão:', error);
      }
    },
    openConfigModal() {
      this.$refs.printerModal.open();
    },
    handleDownloadPluginRequested() {
      this.$refs.printerPluginDownloadModal.open();
    },
  },
};
</script>

<style lang="scss">
@import '../../../../static/stylesheets/variables';
.samples {
  width: 100%;
  margin-top: 70px;

  .subheader_container {
    height: auto !important;
    position: absolute !important;
    top: 80px !important;
  }

  &__subheader {
    height: auto !important;
    width: 100%;
    display: flex;
    justify-content: center;
    flex-shrink: 0;
    flex-grow: 0;
    position: absolute !important;
    background-color: #ebebeb;
    top: 58px;
    z-index: 10;
    left: 0;

    h2 {
      font-size: 22px;
      font-weight: normal;
      letter-spacing: -0.3px;
      text-align: left;
      color: #7a7a7a;
      line-height: 30px;
    }

    .websocket-icon {
      display: flex;
      justify-content: center;
      align-content: center;

      .md-icon {
        padding: 4px;
        width: 45px;
        height: 45px;
        top: -6px;
        right: 0;
        bottom: 0;
        left: -5px;
      }
    }

    .connected {
      color: #4caf50;
    }

    .disconnected {
      color: #f44336;
    }
  }

  .switcher-position {
    width: max-content;
  }

  .print-tag-main-view {
    &__form-patient-name {
      display: flex;
      align-items: flex-start;

      button {
        background-color: #7f1a54;
        min-height: auto;
        height: auto;
        padding: 9px 13px 8px 13px;
        margin-left: 7px;
        font-size: 10px;
        letter-spacing: 0.8px;
        font-family: Roboto;
        font-weight: 500;

        &:hover {
          opacity: 0.9;
        }
      }

      .search-box-container {
        background-color: #fff;
        padding: 5px 10px;
        border-radius: 5px;

        input {
          border: none;
          outline: none;
          min-width: 220px;
        }
      }
    }

    &__row-filter {
      display: flex;
      justify-content: space-between;
    }

    &__empty-cart {
      width: calc(100% - 115px);
    }

    &__filter {
      float: right;
      margin-bottom: 25px;
    }

    .snackbar-custom {
      &.md-snackbar {
        .md-snackbar-container {
          width: auto;
          font-family: Roboto, sans-serif;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          line-height: 26px;
          text-align: center;
          background-color: #fee93e;
          color: #6c3832;
          margin: 0;
          max-width: max-content;
          padding: 11px 24px;
          min-height: min-content;
          border: 1px solid #6c3832;

          .action {
            font-weight: bold;
            text-decoration: underline;
            cursor: pointer;
          }
        }
      }
    }
  }
}

.w-full {
  width: 100%;
}

.flex {
  display: flex;
}

.justify-between {
  justify-content: space-between;
}

.align-center {
  align-items: center;
}

.align-end {
  align-items: end;
}

.gap-2 {
  gap: 0.5rem;
}
</style>
